import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material'
import React from 'react'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import {
  GetCaseLineItemDetail,
  processAction,
  processDynamicAction,
} from 'src/services/WorkflowService'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { SelectDefaultCaseLineItemRequest } from 'src/viewModels/workflow/SelectDefaultCaseLineItemRequest'

interface ISelectDefaultCaseLineItemModalProps {
  closeModal: () => void
  isOpen: boolean
  reloadParent?: () => void
  action: InboxActionSummary
  isDynamicItem: boolean
  inboxItemId: number
  rerouteToPath?: (path: string) => void
  associatedItemId: string
  inboxId: string
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  data: ObjectiveProcessAction
}

class SelectDefaultCaseLineItemModal extends React.Component<ISelectDefaultCaseLineItemModalProps> {
  public state = {
    loading: true,
    executed: false,
    comments: '',
    selectedLineItemId: '',
    lineItemOptions: [] as DropdownOption[],
    errorMessage: '',
  }

  componentDidMount(): void {
    this.fetchLineItems()
  }

  private fetchLineItems = async () => {
    try {
      const caseId = this.props.associatedItemId
      if (!caseId) {
        this.setState({
          errorMessage: 'No case ID available',
          loading: false,
        })
        return
      }

      const response = await GetCaseLineItemDetail(+caseId, false)
      this.setState({
        lineItemOptions: response,
        selectedLineItemId: response.length === 1 ? response[0].id : '',
        loading: false,
      })
    } catch (error) {
      this.setState({
        lineItemOptions: [],
        errorMessage: 'Failed to load line items',
        loading: false,
      })
    }
  }

  public render() {
    const { isOpen, associatedItemId } = this.props

    return (
      <Dialog key={associatedItemId} fullWidth={true} maxWidth="sm" open={isOpen!}>
        {this.state.loading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <DialogTitle style={{ padding: 0 }}>
          <ResponsiveAppBar title={this.props.action.actionName} isModal={true} />
        </DialogTitle>
        <DialogContent>
          <Grid container direction="column" xs={12} style={{ paddingTop: '10px' }}>
            <form>
              <Grid item xs={12} style={{ paddingBottom: '16px' }}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  required
                  error={!!this.state.errorMessage}
                >
                  <InputLabel
                    shrink={true}
                    variant="outlined"
                    style={{ color: this.state.errorMessage ? 'red' : undefined }}
                  >
                    Line Item
                  </InputLabel>
                  <Select
                    variant="outlined"
                    input={<OutlinedInput notched label="Line Item" />}
                    fullWidth
                    native={true}
                    name="lineItemId"
                    value={this.state.selectedLineItemId}
                    onChange={(event) =>
                      this.setState({ selectedLineItemId: event.target.value as string })
                    }
                    disabled={
                      this.state.lineItemOptions.length === 1 ||
                      this.state.lineItemOptions.length === 0
                    }
                  >
                    <option value="">Select a line item</option>
                    {this.state.lineItemOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.description}
                      </option>
                    ))}
                  </Select>
                  {this.state.errorMessage && (
                    <FormHelperText>{this.state.errorMessage}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {this.props.action.displayComment && (
                <Grid item xs={12}>
                  <TextField
                    label="Comments"
                    name="comments"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required={this.props.action.requireComment}
                    multiline
                    minRows={4}
                    value={this.state.comments}
                    onChange={(event) => this.setState({ comments: event.target.value })}
                  />
                </Grid>
              )}
            </form>
          </Grid>
        </DialogContent>
        <DialogActions style={{ display: 'flex' }}>
          <Button
            onClick={this.save}
            color="primary"
            variant="contained"
            type="submit"
            disabled={
              this.state.executed ||
              this.state.selectedLineItemId === '' ||
              (this.props.action.requireComment && this.state.comments === '')
            }
          >
            Execute
          </Button>
          <Button onClick={this.close} data-cy="close">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
  public secondFormRef = React.createRef<any>()

  private close = () => {
    this.setState({
      loading: true,
      executed: false,
      comments: '',
      selectedLineItemId: '',
      lineItemOptions: [],
      errorMessage: '',
    })
    this.props.closeModal!()
  }

  private save = async () => {
    this.setState({ loading: true, executed: true })

    const action = this.props.action
    const data = new ObjectiveProcessAction()

    data.comment = this.state.comments

    const request = new SelectDefaultCaseLineItemRequest()
    request.caseId = +this.props.associatedItemId
    request.caseLineItemId = +this.state.selectedLineItemId
    data.selectDefaultCaseLineItemRequest = request

    if (this.props.isDynamicItem) {
      processDynamicAction(
        data,
        this.props.inboxId.toString(),
        action!.id.toString(),
        this.props.associatedItemId.toString()
      )
        .then(() => {
          this.props.handleProcessActionFinish!(action)
          this.props.closeModal()
        })
        .catch(() => {
          this.setState({ executed: false, loading: false })
        })
    } else {
      processAction(data, action!.id.toString(), this.props.inboxItemId.toString())
        .then(() => {
          this.props.handleProcessActionFinish!(action)
          this.props.closeModal()
        })
        .catch(() => {
          this.setState({ executed: false, loading: false })
        })
    }
  }
}

export default SelectDefaultCaseLineItemModal
